import React, { useMemo, useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./reycle_bin.css";

import useSWR from "swr";

interface DataType {
    key: React.Key;
    id_transaksi: string;
    invoice_no: string;
    amount: number;
    transaction_to: string;
    warehouse: string;
    transaction_type: string;
    transaction_date: string;
    customer_code: string;
    customer_name: string;
    top: string;
    log_status: string;
    log_date: string;
    user_log: string;
}



interface ApiDataType {
    id: number;
    id_trans: string;
    invoice_no: string;
    amount: string;
    transaction_to: string;
    warehouse: string;
    trans_type: string;
    date_trans: string;
    customer_code: string;
    customer_name: string;
    top: string;
    log_status: string;
    log_date: string;
    user_log: string;
}


type DataIndex = keyof DataType;


const backendUrl = process.env.REACT_APP_BACKEND_URL;
const fetcher = (url: string) => fetch(url).then(res => res.json());
const formatRupiah = (value: number) => {
    if (typeof value !== 'number') {
        return value;
    }
    return new Intl.NumberFormat('id-ID').format(value);
};


const ReycleBin: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    
    const { data: apiData, error, isLoading } = useSWR<ApiDataType[]>(`${backendUrl}/api/reycle-bin-data`, fetcher);

    const dataSource = useMemo(() => {
        if (!apiData) return [];
        return apiData.map(item => ({
            key: item.id,
            id_transaksi: item.id_trans,
            invoice_no: item.invoice_no,
            amount: parseFloat(item.amount) || 0,
            transaction_to: item.transaction_to,
            warehouse: item.warehouse,
            transaction_type: item.trans_type,
            transaction_date: item.date_trans,
            customer_code: item.customer_code,
            customer_name: item.customer_name,
            top: item.top,
            log_status: item.log_status,
            log_date: item.log_date,
            user_log: item.user_log,
        }));
    }, [apiData]);
    

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.toString().replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'ID Transaksi',
            dataIndex: 'id_transaksi',
            key: 'id_transaksi',
            sorter: (a, b) => a.id_transaksi.localeCompare(b.id_transaksi),
            ...getColumnSearchProps('id_transaksi'),
        },
        {
            title: 'Log Status',
            dataIndex: 'log_status',
            key: 'log_status',
            sorter: (a, b) => a.log_status.localeCompare(b.log_status),
            ...getColumnSearchProps('log_status'),
        },
        {
            title: 'Log Date',
            dataIndex: 'log_date',
            key: 'log_date',
            sorter: (a, b) => a.log_date.localeCompare(b.log_date),
            ...getColumnSearchProps('log_date'),
        },
        {
            title: 'Transaction Date',
            dataIndex: 'transaction_date',
            key: 'transaction_date',
            sorter: (a, b) => new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime(),
            ...getColumnSearchProps('transaction_date'),
            
            render: (text: string) => text ? new Date(text).toLocaleString('id-ID') : '-',
            
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            sorter: (a, b) => a.invoice_no.localeCompare(b.invoice_no),
            ...getColumnSearchProps('invoice_no'),
        },
        {
            title: 'Transaction Type',
            dataIndex: 'transaction_type',
            key: 'transaction_type',
            sorter: (a, b) => a.transaction_type.localeCompare(b.transaction_type),
            ...getColumnSearchProps('transaction_type'),
        },
        {
            title: 'Customer Code',
            dataIndex: 'customer_code',
            key: 'customer_code',
            sorter: (a, b) => a.customer_code.localeCompare(b.customer_code),
            ...getColumnSearchProps('customer_code'),
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            sorter: (a, b) => a.customer_name.localeCompare(b.customer_name),
            ...getColumnSearchProps('customer_name'),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            sorter: (a, b) => a.amount - b.amount,
            ...getColumnSearchProps('amount'),
            
            render: (text: number) => formatRupiah(text),
            
        },
        {
            title: 'Transaction To',
            dataIndex: 'transaction_to',
            key: 'transaction_to',
            sorter: (a, b) => a.transaction_to.localeCompare(b.transaction_to),
            ...getColumnSearchProps('transaction_to'),
        },
        
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            ...getColumnSearchProps('warehouse'),
        },
        
        {
            title: 'TOP',
            dataIndex: 'top',
            key: 'top',
            sorter: (a, b) => a.top.localeCompare(b.top),
            ...getColumnSearchProps('top'),
        },
        {
            title: 'User Log',
            dataIndex: 'user_log',
            key: 'user_log',
            
            sorter: (a, b) => a.user_log.localeCompare(b.user_log),
            
            ...getColumnSearchProps('user_log'),
        },
    ];


    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="iunf">
                <div className="gjs-grid-column" id="ie35">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Log Reycle Bin Transaction Invoice</span>
                                    </>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="iym7k">
                <div className="gjs-grid-column" id="ijxgd">

                </div>
            </div>

            <div className="gjs-grid-row" id="inekp">
                <div className="gjs-grid-column" id="ie08">
                   <Table<DataType> columns={columns} dataSource={dataSource} loading={isLoading} scroll={{ x: 'max-content' }} />
                </div>
            </div>
        </div>
    );
};

export default ReycleBin;